/**
 * Planos por recursos (feature gating) e limites de uso.
 * A fonte da verdade em produção é a tabela `plans` (editável no superadmin);
 * os valores de DEFAULT_PLANS são usados no seed e como referência de preço.
 */
import { BRAND } from './brand';

export const FEATURES = {
  pos: { label: 'PDV: registrar vendas', group: 'Vendas' },
  offline: { label: 'Vender sem internet', group: 'Vendas' },
  receipts: { label: 'Recibos por WhatsApp e impressão', group: 'Vendas' },
  conditional: { label: 'Condicional (malinha) e trocas com vale-troca', group: 'Vendas' },
  stock: { label: 'Controle de estoque', group: 'Produtos' },
  variants: { label: 'Grade de variações (cor × tamanho)', group: 'Produtos' },
  purchases: { label: 'Compras, fornecedores e importação de NF-e', group: 'Produtos' },
  inventory: { label: 'Inventário com contagem por grade', group: 'Produtos' },
  fiado: { label: 'Controle de fiado com extrato por link', group: 'Clientes' },
  catalog: { label: 'Catálogo online', group: 'Loja online' },
  catalogOrders: { label: 'Receber pedidos pelo catálogo', group: 'Loja online' },
  coupons: { label: 'Cupons de desconto', group: 'Loja online' },
  onlinePayments: { label: 'Pagamento online no catálogo (Pix, cartão e boleto)', group: 'Loja online' },
  shippingCalc: { label: 'Frete por distância e CEP, com descontos por região', group: 'Loja online' },
  removeBranding: { label: `Catálogo sem a marca ${BRAND.name}`, group: 'Loja online' },
  customDomain: { label: 'Domínio próprio no catálogo', group: 'Loja online' },
  integrations: { label: 'Google Shopping e Instagram/Facebook', group: 'Loja online' },
  finance: { label: 'Finanças: caixa, contas a pagar e receber, Raio-X do mês', group: 'Gestão' },
  financeAdvanced: { label: 'Financeiro completo: DRE, metas, conciliação e despesas fixas', group: 'Gestão' },
  reportsAdvanced: { label: 'Estatísticas avançadas (lucro, curva ABC, vendedores)', group: 'Gestão' },
  exports: { label: 'Exportar relatórios (Excel, PDF, CSV)', group: 'Gestão' },
  multiUser: { label: 'Equipe com permissões', group: 'Gestão' },
  auditLog: { label: 'Registro de atividades (auditoria)', group: 'Gestão' },
  ai: { label: 'Cadastro de produtos com IA', group: 'Inteligência' },
  intelligence: { label: 'Inteligência de vendas: matriz de produtos, previsão de estoque e recomendações', group: 'Inteligência' },
  apiAccess: { label: 'API e webhooks (em breve)', group: 'Avançado' },
  prioritySupport: { label: 'Suporte prioritário por WhatsApp', group: 'Avançado' },
} as const;

export type FeatureKey = keyof typeof FEATURES;
export const FEATURE_KEYS = Object.keys(FEATURES) as FeatureKey[];

export const LIMITS = {
  products: { label: 'Produtos cadastrados', unit: '' },
  users: { label: 'Usuários na equipe', unit: '' },
  catalogOrdersPerMonth: { label: 'Pedidos do catálogo por mês', unit: '' },
  activeCoupons: { label: 'Cupons ativos', unit: '' },
  aiCreditsPerMonth: { label: 'Créditos de IA por mês', unit: '' },
  storageMb: { label: 'Armazenamento de imagens', unit: 'MB' },
} as const;

export type LimitKey = keyof typeof LIMITS;
export const LIMIT_KEYS = Object.keys(LIMITS) as LimitKey[];

export type FeatureMap = Record<FeatureKey, boolean>;
/** null = ilimitado */
export type LimitMap = Record<LimitKey, number | null>;

export type SubscriptionStatusKey = 'trialing' | 'active' | 'past_due' | 'canceled' | 'expired';
export type BillingCycleKey = 'monthly' | 'yearly';

export const SUBSCRIPTION_STATUS_LABELS: Record<SubscriptionStatusKey, string> = {
  trialing: 'Em teste grátis',
  active: 'Ativa',
  past_due: 'Pagamento pendente',
  canceled: 'Cancelada',
  expired: 'Expirada',
};

export interface PlanDefinition {
  code: string;
  name: string;
  description: string;
  highlight?: boolean;
  priceMonthlyCents: number;
  priceYearlyCents: number;
  trialDays: number;
  features: FeatureMap;
  limits: LimitMap;
}

const none: FeatureMap = Object.fromEntries(FEATURE_KEYS.map((k) => [k, false])) as FeatureMap;
const all: FeatureMap = Object.fromEntries(FEATURE_KEYS.map((k) => [k, true])) as FeatureMap;

function features(enabled: FeatureKey[]): FeatureMap {
  return { ...none, ...Object.fromEntries(enabled.map((k) => [k, true])) } as FeatureMap;
}

// Diferenciais em TODOS os planos (lacunas do Kyte apontadas pela pesquisa):
// grade cor × tamanho, estoque, fiado, pedidos pelo catálogo e versão web/desktop.
const BASE: FeatureKey[] = [
  'pos', 'offline', 'receipts', 'stock', 'variants', 'fiado', 'catalog', 'catalogOrders', 'coupons', 'ai',
];
const ESSENTIAL: FeatureKey[] = [
  ...BASE, 'conditional', 'finance', 'exports', 'multiUser', 'onlinePayments', 'shippingCalc', 'purchases', 'inventory',
];
const PRO: FeatureKey[] = [
  ...ESSENTIAL, 'reportsAdvanced', 'removeBranding', 'customDomain', 'integrations', 'auditLog', 'financeAdvanced', 'intelligence',
];

/**
 * Estrutura de preços — ver docs/PRICING.md (pesquisa de 29/09/2026).
 * Referências: Kyte PRO R$49,90 / GROW R$69,90 / PRIME R$99,90; Stoqui R$39/59/109.
 * Posicionamento: cada plano entrega mais que o equivalente do Kyte por menos.
 * Anual = 20% de desconto (pagamento via Pix ou 12x no cartão).
 */
export const DEFAULT_PLANS: PlanDefinition[] = [
  {
    code: 'gratis',
    name: 'Grátis',
    description: 'Para começar a vender: PDV, estoque, fiado e catálogo com pedidos.',
    priceMonthlyCents: 0,
    priceYearlyCents: 0,
    trialDays: 0,
    features: features(BASE),
    limits: { products: 100, users: 1, catalogOrdersPerMonth: 50, activeCoupons: 1, aiCreditsPerMonth: 20, storageMb: 300 },
  },
  {
    code: 'essencial',
    name: 'Essencial',
    description: 'Para quem vende todo dia na loja e no WhatsApp, com equipe pequena.',
    priceMonthlyCents: 2990,
    priceYearlyCents: 28690,
    trialDays: 14,
    features: features(ESSENTIAL),
    limits: { products: 1000, users: 2, catalogOrdersPerMonth: null, activeCoupons: 5, aiCreditsPerMonth: 100, storageMb: 2048 },
  },
  {
    code: 'pro',
    name: 'Pro',
    description: 'Gestão completa: relatórios avançados, domínio próprio e integrações.',
    highlight: true,
    priceMonthlyCents: 4990,
    priceYearlyCents: 47890,
    trialDays: 14,
    features: features(PRO),
    limits: { products: null, users: 5, catalogOrdersPerMonth: null, activeCoupons: null, aiCreditsPerMonth: 400, storageMb: 10240 },
  },
  {
    code: 'max',
    name: 'Max',
    description: 'Para lojas com equipe grande e atendimento prioritário.',
    priceMonthlyCents: 8990,
    priceYearlyCents: 86290,
    trialDays: 14,
    features: all,
    limits: { products: null, users: null, catalogOrdersPerMonth: null, activeCoupons: null, aiCreditsPerMonth: 1500, storageMb: 51200 },
  },
];

export const FREE_PLAN_CODE = 'gratis';
/** Plano liberado durante o teste grátis de novas contas (reverse trial). */
export const TRIAL_PLAN_CODE = 'pro';
export const TRIAL_DAYS = 14;
/** Dias de tolerância após o vencimento antes de voltar ao plano grátis. */
export const PAST_DUE_GRACE_DAYS = 7;

export interface Overrides {
  features?: Partial<FeatureMap>;
  limits?: Partial<LimitMap>;
}

export interface Entitlements {
  planCode: string;
  planName: string;
  status: SubscriptionStatusKey;
  billingCycle: BillingCycleKey;
  features: FeatureMap;
  limits: LimitMap;
  trialEndsAt: string | null;
  currentPeriodEnd: string | null;
  /** true quando a assinatura venceu e a loja está usando o plano grátis */
  downgraded: boolean;
}

/** Normaliza um JSON de features vindo do banco (chaves ausentes = false). */
export function normalizeFeatures(raw: unknown): FeatureMap {
  const obj = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  return Object.fromEntries(FEATURE_KEYS.map((k) => [k, obj[k] === true])) as FeatureMap;
}

/** Normaliza um JSON de limites vindo do banco (ausente/null = ilimitado). */
export function normalizeLimits(raw: unknown): LimitMap {
  const obj = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  return Object.fromEntries(
    LIMIT_KEYS.map((k) => [k, typeof obj[k] === 'number' ? (obj[k] as number) : null]),
  ) as LimitMap;
}

export function computeEntitlements(input: {
  plan: { code: string; name: string; features: unknown; limits: unknown };
  freePlan: { code: string; name: string; features: unknown; limits: unknown };
  status: SubscriptionStatusKey;
  billingCycle?: BillingCycleKey;
  overrides?: unknown;
  trialEndsAt?: Date | string | null;
  currentPeriodEnd?: Date | string | null;
}): Entitlements {
  const active = input.status === 'trialing' || input.status === 'active' || input.status === 'past_due';
  const base = active ? input.plan : input.freePlan;
  const ov = (input.overrides ?? {}) as Overrides;
  const feats = { ...normalizeFeatures(base.features), ...(ov.features ?? {}) } as FeatureMap;
  const lims = { ...normalizeLimits(base.limits), ...(ov.limits ?? {}) } as LimitMap;
  const iso = (d: Date | string | null | undefined) => (d ? new Date(d).toISOString() : null);
  return {
    planCode: base.code,
    planName: base.name,
    status: input.status,
    billingCycle: input.billingCycle ?? 'monthly',
    features: feats,
    limits: lims,
    trialEndsAt: iso(input.trialEndsAt),
    currentPeriodEnd: iso(input.currentPeriodEnd),
    downgraded: !active && input.plan.code !== input.freePlan.code,
  };
}

export function isWithinLimit(limit: number | null | undefined, current: number, adding = 1): boolean {
  if (limit === null || limit === undefined) return true;
  return current + adding <= limit;
}

/** Economia anual em % em relação a 12x o mensal. */
export function yearlySavingsPercent(monthlyCents: number, yearlyCents: number): number {
  if (!monthlyCents) return 0;
  return Math.round((1 - yearlyCents / (monthlyCents * 12)) * 100);
}

/** Menor plano público que libera um recurso (para mensagens de upgrade). */
export function cheapestPlanWith<T extends { code: string; name: string; priceMonthlyCents: number; features: unknown }>(
  plans: T[],
  feature: FeatureKey,
): T | undefined {
  return [...plans]
    .filter((p) => normalizeFeatures(p.features)[feature])
    .sort((a, b) => a.priceMonthlyCents - b.priceMonthlyCents)[0];
}
